/**
 * One shared IntersectionObserver for every landing reveal (DESIGN.md §7 pattern 2). Sharing a single
 * observer rather than creating one per node is what keeps the landing page at 60 fps on a mid-range
 * laptop; elements stop observing once they have appeared because a reveal is not a toggle.
 */
import { useEffect, useRef, useState } from 'react';



let observer: IntersectionObserver | null = null;
const callbacks = new WeakMap<Element, (visible: boolean) => void>();

const ensureObserver = (): IntersectionObserver | null => {
  if (typeof IntersectionObserver === 'undefined') return null;
  observer ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        callbacks.get(entry.target)?.(true);
        observer?.unobserve(entry.target);
        callbacks.delete(entry.target);
      }
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.12 },
  );
  return observer;
};

export function useInView<T extends HTMLElement = HTMLDivElement>(options: { once?: boolean; enabled?: boolean } = {}): { ref: (node: T | null) => void; inView: boolean } {
  const { once = true, enabled = true } = options;
  const [inView, setInView] = useState(false);
  const nodeRef = useRef<T | null>(null);

  useEffect(() => {
    if (!enabled) {
      setInView(true);
      return;
    }
    const obs = ensureObserver();
    if (!obs) {
      setInView(true);
      return;
    }
    const node = nodeRef.current;
    if (!node) return;
    callbacks.set(node, (visible) => setInView(visible));
    obs.observe(node);
    return () => {
      if (once) callbacks.delete(node);
      obs.unobserve(node);
    };
  }, [enabled, once]);

  return {
    ref: (node: T | null) => {
      nodeRef.current = node;
    },
    inView,
  };
}

export const prefersReducedMotion = (): boolean => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
export const hasHover = (): boolean => typeof window !== 'undefined' && window.matchMedia?.('(hover: hover)').matches === true;
